import { useContext, createContext, useMemo } from "react";
import { Location } from "react-router-dom";
import type {
  User,
  UserClass,
  OrgHelper,
  AccessHelper,
} from "@propelauth/react";

/**
 * History adapter interface for react-router-dom v7 compatibility.
 * This replaces the old History type from the `history` package.
 */
export interface HistoryAdapter {
  push: (to: string | { search: string }) => void;
  replace: (to: string | { search: string }) => void;
  goBack?: () => void;
}

export type IRouterContext = {
  history: HistoryAdapter;
  location?: Location;
};

/**
 * Type for the navigate function parameter.
 * Compatible with react-router-dom's useNavigate() return type.
 */
type NavigateFn = {
  (to: string | { search: string }, options?: { replace?: boolean }): void;
  (delta: number): void;
};

/**
 * Creates a history adapter for react-router-dom v7 useNavigate hook.
 * This provides backward compatibility with code that expects a history-like API.
 *
 * @param navigate - The navigate function from useNavigate()
 * @returns A history adapter object with push, replace, and goBack methods
 *
 * @example
 * ```tsx
 * const navigate = useNavigate();
 * const history = useHistoryAdapter(navigate);
 * ```
 */
export const useHistoryAdapter = (navigate: NavigateFn): HistoryAdapter => {
  return useMemo(
    () => ({
      push: (to: string | { search: string }) => {
        if (typeof to === "string") {
          navigate(to);
        } else {
          navigate({ search: to.search });
        }
      },
      replace: (to: string | { search: string }) => {
        if (typeof to === "string") {
          navigate(to, { replace: true });
        } else {
          navigate({ search: to.search }, { replace: true });
        }
      },
      goBack: () => navigate(-1),
    }),
    [navigate],
  );
};

export const RouterContext = createContext<IRouterContext>(
  {} as IRouterContext,
);

export const AuthContext = createContext<{
  accessToken: string | null;
  ssoUser: User | null;
  isImpersonating: boolean | null;
  accessTokenExpiresAtSeconds: number | null;
  isLoggedIn: boolean | null;
  userClass: UserClass | null;
  orgHelper: OrgHelper | null;
  impersonatorUserId: string | null;
  accessHelper: AccessHelper | null;
}>({
  accessToken: null,
  ssoUser: null,
  isImpersonating: null,
  accessTokenExpiresAtSeconds: null,
  isLoggedIn: null,
  userClass: null,
  orgHelper: null,
  impersonatorUserId: null,
  accessHelper: null,
});

export const useAuth = () => useContext(AuthContext);
